import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from "recharts"
import {
  formatCurrency, formatNumber, formatPercent,
  familiaConfig,
} from "../helpers"

/**
 * Vista por familia con detalle agregado de tipos.
 * Calcula client-side el aporte de cada tipo dentro de su familia
 * agregando los `impuestos[]` de todas las sucursales.
 */
const FamiliasTab = ({ data }) => {
  const [familiaActiva, setFamiliaActiva] = useState("")

  const familias = data?.por_familia || []

  // Construye agregado de tipos × familia desde `sucursales[].impuestos[]`
  const tiposAgregados = useMemo(() => {
    const map = new Map()
    const sucursales = data?.sucursales || []
    sucursales.forEach(s => {
      (s.impuestos || []).forEach(imp => {
        const key = `${imp.familia}::${imp.tipo_impuesto}::${imp.tasa}`
        const prev = map.get(key) || {
          familia: imp.familia,
          tipo_impuesto: imp.tipo_impuesto,
          nombre_impuesto: imp.nombre_impuesto,
          tasa: imp.tasa,
          cantidad_lineas: 0,
          base_gravable: 0,
          valor_impuesto: 0,
          total_con_impuesto: 0,
          sucursales_count: 0,
        }
        prev.cantidad_lineas += Number(imp.cantidad_lineas) || 0
        prev.base_gravable   += Number(imp.base_gravable)   || 0
        prev.valor_impuesto  += Number(imp.valor_impuesto)  || 0
        prev.total_con_impuesto += Number(imp.total_con_impuesto) || 0
        prev.sucursales_count += 1
        map.set(key, prev)
      })
    })
    return Array.from(map.values()).sort((a, b) => b.valor_impuesto - a.valor_impuesto)
  }, [data])

  const tiposFiltrados = useMemo(() => {
    if (!familiaActiva) return tiposAgregados
    return tiposAgregados.filter(t => t.familia === familiaActiva)
  }, [tiposAgregados, familiaActiva])

  if (!data) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-tag-multiple-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin datos.</div>
      </div>
    )
  }

  if (familias.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-tag-off-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">No hay familias de impuesto en el período.</div>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Cards por familia */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <button
          type="button"
          onClick={() => setFamiliaActiva("")}
          style={{
            background: !familiaActiva ? "#fff" : "#f8fafc",
            border: !familiaActiva ? "2px solid #3b82f6" : "1px solid #e2e8f0",
            borderRadius: 14, padding: "14px 16px", cursor: "pointer",
            textAlign: "left", boxShadow: !familiaActiva ? "0 2px 6px #3b82f626" : "none",
            transition: "all 0.15s",
          }}
        >
          <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>
            <i className="mdi mdi-view-grid-outline me-1" />
            Todas las familias
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 4 }}>
            {tiposAgregados.length} <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>tipos</span>
          </div>
        </button>

        {familias.map(f => {
          const cfg = familiaConfig(f.familia)
          const activa = familiaActiva === f.familia
          return (
            <button
              key={f.familia}
              type="button"
              onClick={() => setFamiliaActiva(f.familia)}
              style={{
                background: activa ? "#fff" : cfg.bg,
                border: activa ? `2px solid ${cfg.color}` : "1px solid transparent",
                borderRadius: 14, padding: "14px 16px", cursor: "pointer",
                textAlign: "left", boxShadow: activa ? `0 2px 6px ${cfg.color}40` : "none",
                transition: "all 0.15s",
              }}
            >
              <div style={{ fontSize: 11, color: cfg.color, textTransform: "uppercase", fontWeight: 700 }}>
                {f.familia} · {cfg.desc}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: cfg.color }}>
                  {formatCurrency(f.valor_impuesto)}
                </span>
              </div>
              <div style={{ fontSize: 11, color: cfg.color, marginTop: 4, opacity: 0.85 }}>
                {formatPercent(f.participacion_pct, 1)} del total · {f.cantidad_tipos} {f.cantidad_tipos === 1 ? "tipo" : "tipos"}
              </div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                Base: {formatCurrency(f.base_gravable)}
              </div>
            </button>
          )
        })}
      </div>

      {/* Detalle de tipos (filtrado por familia activa) */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
        <div className="d-flex justify-content-between align-items-baseline mb-2 flex-wrap gap-2">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-format-list-bulleted-type me-2 text-primary" />
            Tipos de impuesto {familiaActiva ? `· familia ${familiaActiva}` : "· todas las familias"}
          </h6>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            {tiposFiltrados.length} {tiposFiltrados.length === 1 ? "tipo" : "tipos"}
          </span>
        </div>
        {tiposFiltrados.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>Sin tipos en este filtro.</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={Math.max(220, tiposFiltrados.length * 32 + 60)}>
              <BarChart
                data={tiposFiltrados.map(t => ({
                  nombre: `${t.tipo_impuesto} · ${t.tasa}%`,
                  nombreCompleto: t.nombre_impuesto,
                  valor: t.valor_impuesto,
                  base: t.base_gravable,
                  familia: t.familia,
                }))}
                layout="vertical" margin={{ left: 30, right: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v).replace("COP", "").trim()} />
                <YAxis type="category" dataKey="nombre" tick={{ fontSize: 11 }} width={100} />
                <Tooltip
                  formatter={(v, n) => [formatCurrency(v), n === "valor" ? "Valor impuesto" : "Base gravable"]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.nombreCompleto || ""}
                />
                <Bar dataKey="valor" radius={[0, 6, 6, 0]}>
                  {tiposFiltrados.map((t, i) => (
                    <Cell key={i} fill={familiaConfig(t.familia).color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            <div style={{ marginTop: 12, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Familia</th>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Tipo</th>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Nombre</th>
                    <th style={{ padding: "8px 10px", textAlign: "center", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Tasa</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Líneas</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Base</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Impuesto</th>
                    <th style={{ padding: "8px 10px", textAlign: "center", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Sedes</th>
                  </tr>
                </thead>
                <tbody>
                  {tiposFiltrados.map((t, i) => {
                    const cfg = familiaConfig(t.familia)
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
                          <span style={{
                            padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
                            background: cfg.bg, color: cfg.color,
                          }}>{t.familia}</span>
                        </td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9", fontFamily: "monospace", fontWeight: 600, color: "#1e40af" }}>
                          {t.tipo_impuesto}
                        </td>
                        <td style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.nombre_impuesto}>
                          {t.nombre_impuesto}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "center", borderBottom: "1px solid #f1f5f9", fontWeight: 600 }}>
                          {formatPercent(t.tasa, 0)}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: "1px solid #f1f5f9" }}>
                          {formatNumber(t.cantidad_lineas)}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: "1px solid #f1f5f9" }}>
                          {formatCurrency(t.base_gravable)}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: "1px solid #f1f5f9", fontWeight: 600, color: "#10b981" }}>
                          {formatCurrency(t.valor_impuesto)}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "center", borderBottom: "1px solid #f1f5f9", fontSize: 11, color: "#64748b" }}>
                          {t.sucursales_count}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

FamiliasTab.propTypes = { data: PropTypes.object }

export default FamiliasTab
